import React, { useState, useEffect } from 'react';
import { APP_STORE_LINKS } from '../constants/appLinks';
import { AI_QUESTIONS, FEATURE_QUESTIONS } from '../data/aiQuestions';

const AnimatedQuestion: React.FC<{ questions: string[] }> = ({ questions }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);

  useEffect(() => {
    const currentQuestion = questions[currentIndex];
    let charIndex = 0;
    setDisplayText('');
    setIsTyping(true);

    const typeInterval = setInterval(() => {
      if (charIndex < currentQuestion.length) {
        setDisplayText(currentQuestion.slice(0, charIndex + 1));
        charIndex++;
      } else {
        setIsTyping(false);
        clearInterval(typeInterval);
        setTimeout(() => {
          setCurrentIndex(prev => (prev + 1) % questions.length);
        }, 2000);
      }
    }, 80);

    return () => clearInterval(typeInterval);
  }, [currentIndex, questions]);

  return <>{`${displayText}${isTyping ? '|' : ''}`}</>;
};

interface FeaturesProps {
  isHomePage?: boolean;
}

const features = [
  {
    emoji: '🤖',
    title: 'AI Assistant',
    description: 'Get instant answers about your community in any language',
    tag: 'Ask in Any Language',
    questions: AI_QUESTIONS,
  },
  {
    emoji: '👥',
    title: 'Groups',
    description: 'Join interest-based groups and meet like-minded people in your area',
    tag: 'Search Groups in any city',
    questions: FEATURE_QUESTIONS.groups,
  },
  {
    emoji: '🎉',
    title: 'Events',
    description: 'Discover local events, workshops, and gatherings happening near you',
    tag: 'Never Miss an Event',
    questions: FEATURE_QUESTIONS.events,
  },
  {
    emoji: '🛠️',
    title: 'Services',
    description: 'Find trusted local service providers and offer your own skills to the community',
    tag: 'Verified Services',
    questions: FEATURE_QUESTIONS.services,
  },
  {
    emoji: '🛒',
    title: 'Buy & Sell',
    description: 'Trade items safely within your trusted community network',
    tag: 'Trusted Network',
    questions: FEATURE_QUESTIONS.marketplace,
  },
  {
    emoji: '🌐',
    title: 'Live Assist',
    description: 'Real-time AI translation during calls — communicate in any language with confidence',
    tag: '90+ Languages',
    questions: null,
  },
];

const Features: React.FC<FeaturesProps> = ({ isHomePage = false }) => {
  return (
    <section className={`px-6 ${isHomePage ? 'py-12' : 'py-14'} bg-indigo-50`}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-indigo-600 text-xs font-semibold uppercase tracking-widest mb-2">Features</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Be In the Know</h2>
          <p className="text-gray-500 max-w-md mx-auto">
            Join local communities and discover what's happening in your neighborhood
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feature, index) => (
            <div key={index} className="bg-white rounded-2xl border border-indigo-100 px-5 py-5 shadow-sm flex flex-col">
              <div className="w-10 h-10 rounded-2xl bg-indigo-100 flex items-center justify-center text-xl mb-4">
                {feature.emoji}
              </div>
              <div className="inline-block text-xs font-semibold text-indigo-600 bg-indigo-50 px-2.5 py-0.5 rounded-full mb-3 self-start">
                {feature.tag}
              </div>
              <h3 className="font-bold text-gray-900 mb-2">{feature.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed mb-4">{feature.description}</p>

              {feature.questions ? (
                <div className="mt-auto flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2">
                  <span className="text-xs text-gray-500 flex-1 truncate">
                    <AnimatedQuestion questions={feature.questions} />
                  </span>
                  <a
                    href="https://chat.sahayy.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-indigo-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg whitespace-nowrap hover:bg-indigo-500 transition-colors"
                  >
                    ✨ Ask
                  </a>
                </div>
              ) : (
                <div className="mt-auto bg-indigo-50 border border-indigo-100 rounded-xl px-3 py-2 text-xs text-indigo-400 italic">
                  Available on iOS & Android
                </div>
              )}
            </div>
          ))}
        </div>

        {!isHomePage && (
          <div className="text-center mt-10">
            <a
              href={APP_STORE_LINKS.APPLE_APP_STORE}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-indigo-600 text-white font-semibold px-8 py-3.5 rounded-2xl hover:bg-indigo-500 transition-colors"
            >
              Download Sahayy →
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default Features;
